import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  position: fixed;
  top: 52px;
  left: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 9998;
`;

const IdText = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  color: #1a5276;
  letter-spacing: 0.3px;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: #f3f4f6;
    color: #1a5276;
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CopiedToast = styled.div`
  position: fixed;
  top: 52px;
  left: 200px;
  background: #10b981;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease;
`;

interface ApplicationIdDisplayProps {
  applicationId: string;
}

const ApplicationIdDisplay: React.FC<ApplicationIdDisplayProps> = ({ applicationId }) => {
  const [showCopied, setShowCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!applicationId) return;
    
    try {
      await navigator.clipboard.writeText(applicationId);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy application ID:', err);
      const textArea = document.createElement('textarea');
      textArea.value = applicationId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    }
  };

  if (!applicationId) return null;

  return (
    <>
      <Container>
        <IdText>{applicationId}</IdText>
        <CopyButton onClick={handleCopy} title="Copy Application ID">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </CopyButton>
      </Container>
      {showCopied && (
        <CopiedToast>Copied!</CopiedToast>
      )}
    </>
  );
};

export default ApplicationIdDisplay;
